import { useQuery } from 'react-query';
import { AxiosError } from 'axios';
import { useTranslation } from 'react-i18next';

import { useApiContext } from 'context/ApiContext';
import { KEYS } from 'helpers/react-query.config';
import { useGlobalContext } from 'context/GlobalContext';

interface Props {
  enabled?: boolean;
}

/**
 * Hook para obtener todos los partners habilitados desde el catálogo.
 *
 * Realiza una consulta a la API para traer los partners habilitados.
 * Maneja errores y alertas según la respuesta del servidor.
 *
 * @param {object} params - Parámetros de configuración.
 * @param {boolean} [params.enabled] - Si la consulta está habilitada.
 * @returns {object} Objeto con los datos de los partners y estado de carga.
 * @example
 * const { allPartners, isAllPartnersLoading } = useGetAllPartners({ enabled: true });
 */
const useGetAllPartners = ({ enabled }: Props) => {
  const { showApiError, showApiAlert } = useGlobalContext();
  const { catalog: CatalogueApi } = useApiContext();
  const { t } = useTranslation('global');

  const { data, isLoading } = useQuery(
    [KEYS.catalog.partners.getAllPartners()],
    () => CatalogueApi.partner.getAllPartnersEnabled({ onlyEnabled: true }),
    {
      select: (response) => response?.data?.data,
      enabled,
      onError: (error: AxiosError) =>
        error?.response?.request?.status === 500
          ? showApiError(error, error?.message || t('errorMessages.partners'))
          : showApiAlert(
              error,
              error?.response?.data?.detail || t('errorMessages.partners')
            ),
      refetchOnWindowFocus: false
    }
  );

  return {
    allPartners: data,
    isAllPartnersLoading: isLoading
  };
};

export default useGetAllPartners;
